<template>
  <div :id="id + '_fs'" :class="_fs_class">
    <cat-label
      :labelId="id + '_lbl'"
      :forId="id"
      :label="label"
      :optional="!mandatory"
      :placeholder="offscreenLabel"
      :errors="errors"
      :readonly="readonly"
    ></cat-label>
    <cat-hint :id="id + '_hint'" :text="hint" />
    <slot></slot>
    <div :class="_holder_class">
      <span class="dropdown-indicator" tabindex="-1" aria-hidden="true"></span>
      <input
        :id="id"
        v-model="model"
        type="text"
        :title="label"
        :aria-required="mandatory"
        :required="errors && errors.length > 0"
        :aria-invalid="errors && errors.length > 0"
        :readonly="readonly"
        :disabled="hasSelected"
        :aria-labelledby="label ? null : id + '_lbl'"
        :aria-describedby="hint ? id + '_hint' : null"
        :placeholder="placeholder"
        :autocomplete="autofillToken"
        @blur="_onBlur"
        @focus="_onFocus"
      />
      <cat-btn
        v-if="hasSelected"
        :right="true"
        :link="true"
        @ui-click="_clear"
        >{{ changeText }}</cat-btn
      >
    </div>
    <slot name="after-input"></slot>
    <cat-errors v-if="errors && errors.length > 0" :errors="errors" />
    <div role="presentation" class="spacer"></div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import jQuery from "jquery";

// From require
// import "jquery-ui-dist/jquery-ui";

import CatErrors from "@/components/catErrors/catErrors.vue";
import CatBtn from "@/components/catBtn/catBtn.vue";
import CatLabel from "@/components/utilities/cat-label.vue";
import CatHint from "@/components/utilities/hint.vue";

import SelectMixin from "./select-mixin.js";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);

    return {
      resolveTxt,
      displayParams
    };
  },
  components: {
    CatErrors,
    CatBtn,
    CatLabel,
    CatHint
  },
  name: "CatAttributeSelectOrNew",
  mixins: [SelectMixin],
  props: {
    id: [String],
    errors: [Array],
    label: [String],
    items: [Array],
    hint: [String],
    placeholder: [String],
    offscreenLabel: [String],
    value: [String],
    changeLabel: {
      type: String
    },
    mandatory: {
      type: Boolean,
      default: false
    },
    size: [String],
    readonly: [Boolean],
    autocompleteFunction: [Function, Array],
    minInputLength: {
      type: [Number, String],
      default: 2
    },
    hasSelected: {
      type: Boolean,
      default: false
    },
    autofillToken: { type: String, default: "off" }
  },
  data: function () {
    return {
      model: this.value ? this.value : "",
      changeText: this.changeLabel
        ? this.changeLabel
        : this.resolveTxt("skin.autocomplete.change", "Change")
    };
  },
  watch: {
    value: function (newval, oldvalue) {
      //   console.log("name changed", newval);
      if (this.model != newval) {
        this.model = newval;
      }
    }
  },
  methods: {
    _sendChangeEvent: function (value, text) {
      this.$emit("ui-change", {
        value: value,
        id: this.id ? this.id : "",
        el: this.$el,
        text: this.model
      });
    },
    _clear: function () {
      this.model = null;
      this._onChange(null, null);
    },
    _onBlur: function (event) {
      this.$emit("ui-blur", {
        event: event,
        id: this.id
      });
    },
    _onFocus: function (event) {
      if (
        !this.model &&
        !isNaN(
          parseInt(this.minInputLength) && parseInt(this.minInputLength) === 0
        )
      ) {
        jQuery("#" + this.id).autocomplete("search", "");
      }
    },
    autocompleteChange: function (value, text) {
      //   console.log("chg", value, text);
      if (value == this.model) {
        return;
      }
      this.model = text;
      this._onChange(value, text);
    },
    autocompleteCreate: function (value) {
      this.model = value;
      //   console.log("create", this.model);
      this.$emit("ui-create", {
        id: this.id ? this.id : "",
        text: this.model,
        el: this.$el,
        value: null
      });
    }
  },
  computed: {
    _fs_class: function () {
      return {
        fieldset: true,
        "cat-attribute-select-or-new": true,
        "cat-select-autosuggest": true,
        "no-label": !this.label,
        required: this.mandatory,
        optional: !this.mandatory,
        "readonly-list": this.readonly,
        "autosuggest-select": this.minInputLength < 1
      };
    },
    _holder_class: function () {
      return {
        "ac-holder": true,
        small: this.size == "small",
        medium: !this.size || this.size == "medium",
        large: this.size == "large"
      };
    }
  },

  mounted: function () {
    const _this = this;
    const options = {
      select: (event, ui) => {
        this.autocompleteChange(ui.item.value, ui.item.label);
      },
      change: (event, ui) => {
        if (ui.item == null) {
          this.autocompleteCreate(event.target.value);
        } else {
          if (ui.item.label !== this.model) {
            this.autocompleteChange(ui.item.value, ui.item.label);
          }
        }
      },
      focus: (event) => {
        // stops it from autoselecting on focus.  We might turn this back on but will need to make it select the label rather than the value.
        event.preventDefault();
      },
      minLength: parseInt(this.minInputLength)
    };
    if (typeof this.autocompleteFunction === "function") {
      options.source = (request, response) => {
        this.autocompleteFunction(request.term).then((data) => {
          response(
            data.results.map((item) => {
              return {
                value: item.id,
                label: item.text,
                avatar: item.avatar,
                extraLines: item.lines
              };
            })
          );
        });
      };
    } else {
      options.source = this.autocompleteFunction;
    }
    var _autocomp = jQuery("#" + this.id).autocomplete(options);
    _autocomp.data("ui-autocomplete")._renderItem = function (ul, item) {
      var hasAvatar = item.avatar && item.avatar !== "";
      var hasLines = item.extraLines && item.extraLines.length > 0;
      var optionRow =
        "<li class='ui-menu-item option'" +
        (hasAvatar ? " avatar" : "") +
        "><div id='ui-id-" +
        item.value +
        "' " +
        " class='ui-menu-item-wrapper'>";
      if (hasAvatar) {
        optionRow +=
          "<span class='avatar'><i class='icon icon-" +
          item.avatar +
          "'></i></span>";
      }
      optionRow +=
        "<span class='option-row'><span class='line text-bold caption'>" +
        item.label +
        "</span>";
      if (hasLines) {
        for (var i = 0; i < item.extraLines.length; i++) {
          optionRow +=
            '<span class="line line-' +
            i +
            '">' +
            item.extraLines[i] +
            "</span>";
        }
      }
      optionRow += "<span/></div></li>";
      return jQuery(optionRow).appendTo(ul);
    };
  }
});
</script>
